import React, { useRef, useState } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import { ParagraphMedium } from '../../../../../core/components';
import { NumberInputField } from '../../../../../core/components/form';
import { MultiToggle } from '../../../../../core/components/toggle';
import simulatorConfig from '../../../constants/simulator-config-constants';
import { ExperimentConfiguration, UniversalParameterProps } from '../../../models';
import { ModalSectionContainer } from '../modal-section-container';
import type { ModalProps } from './patient-settings-modal';

type MotionSectionProps = Pick<ModalProps, 'config' | 'onSubmit' | 'motionParameters'> & {
  setMotionType: ModalProps['updatePatientSettings'];
};

const MOTION_TYPE_OPTIONS = [
  {
    id: 'none',
    label: 'None',
  },
  {
    id: 'translational',
    label: 'Translational',
  },
  {
    id: 'rotational',
    label: 'Rotational',
  },
];

export const MotionSection = ({
  config,
  motionParameters,
  onSubmit,
  setMotionType,
}: MotionSectionProps): JSX.Element => {
  const [isActive, setIsActive] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const motionType = config?.find((c: ExperimentConfiguration) => c.id === 30)?.selectedValue;

  const onMotionSelectHandler = (motion: string) => {
    setMotionType('true', motion, simulatorConfig.MOTION);
  };

  const initial: UniversalParameterProps = {};
  motionParameters.forEach((p) => {
    initial[p.name] = p.selectedValue;
  });

  const possibleValues = [
    { id: 1, label: 'along readout', value: '1' },
    { id: 2, label: 'along phase', value: '2' },
  ];

  return (
    <ModalSectionContainer title="Motion">
      <div className="col-10">
        <MultiToggle items={MOTION_TYPE_OPTIONS} onSelect={onMotionSelectHandler} selectedItemKey={motionType} />
      </div>

      {motionType !== 'none' && (
        <div className="settings-modal__form-wrapper">
          <ParagraphMedium className="m-0">{`Select which ${motionType} settings you wish to use`}</ParagraphMedium>
          <Form
            onSubmit={onSubmit}
            initialValues={initial}
            mutators={{
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              setFormValue: ([field, value]: any, state, { changeValue }) => {
                changeValue(state, field, () => value);
              },
            }}
            render={({ handleSubmit, form }) => (
              <form onSubmit={handleSubmit} className="d-flex justify-content-start my-2">
                <FormSpy
                  subscription={{ values: true, active: true }}
                  onChange={(state) => {
                    if (!!state.active !== isActive) {
                      const valRotAngle = state.values.motion_rot_angle;
                      if (valRotAngle > 45 || valRotAngle < 1) {
                        form.mutators.setFormValue('motion_rot_angle', initial?.motion_rot_angle);
                        return;
                      }

                      const valRotFreq = state.values.motion_rot_freq;
                      if (valRotFreq > 10 || valRotFreq < 1) {
                        form.mutators.setFormValue('motion_rot_freq', initial?.motion_rot_freq);
                        return;
                      }

                      const valTransMagn = state.values.motion_trans_magn;
                      if (valTransMagn > 0.1 || valTransMagn < 0.001) {
                        form.mutators.setFormValue('motion_trans_magn', initial?.motion_trans_magn);
                        return;
                      }

                      const valTransFreq = state.values.motion_trans_freq;
                      if (valTransFreq > 10 || valTransFreq < 1) {
                        form.mutators.setFormValue('motion_trans_freq', initial?.motion_trans_freq);
                        return;
                      }
                      // blur
                      handleSubmit();
                      setIsActive(!isActive);
                    }
                  }}
                />
                <div className="py-0 mt-4 mb-3 flex-fill settings-modal-form" ref={contentRef}>
                  {motionType === 'rotational' && (
                    <div className="row">
                      <div className="col-5">
                        <NumberInputField
                          type="number"
                          selValue={parseInt(motionParameters[0].selectedValue, 10)}
                          label="Rotational flip angle [degrees]"
                          name="motion_rot_angle"
                          stepValue={1}
                          min={1}
                          max={45}
                          showTooltip
                        />
                      </div>
                      <div className="col-5">
                        <NumberInputField
                          type="number"
                          selValue={parseInt(motionParameters[1].selectedValue, 10)}
                          label="Rotational frequence [Hz]"
                          name="motion_rot_freq"
                          stepValue={1}
                          min={1}
                          max={10}
                          showTooltip
                        />
                      </div>
                    </div>
                  )}
                  {motionType === 'translational' && (
                    <div className="row">
                      <div className="col-4">
                        <NumberInputField
                          type="number"
                          selValue={parseFloat(motionParameters[2].selectedValue)}
                          label="Translational displacement [m]"
                          name="motion_trans_magn"
                          stepValue={0.001}
                          min={0.001}
                          max={0.1}
                          showTooltip
                        />
                      </div>
                      <div className="col-4">
                        <NumberInputField
                          type="number"
                          selValue={parseInt(motionParameters[3].selectedValue, 10)}
                          label="Translational frequence [Hz]"
                          name="motion_trans_freq"
                          stepValue={1}
                          min={1}
                          max={10}
                          showTooltip
                        />
                      </div>
                      <div className="col-4">
                        <div className="form-group">
                          <div className="row">
                            <div className="col">
                              <label htmlFor="motion_trans_axis">Translational axis</label>
                            </div>
                          </div>
                          <div className="form-label-group">
                            <Field
                              component="select"
                              label="Translational axis"
                              name="motion_trans_axis"
                              className="form-control"
                              defaultValue={motionParameters[4].selectedValue.toString()}
                            >
                              {possibleValues.map((option) => {
                                return (
                                  <option key={option.id} value={option.value}>
                                    {option.label}
                                  </option>
                                );
                              })}
                            </Field>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            )}
          />
        </div>
      )}
    </ModalSectionContainer>
  );
};
